import { atom } from "@reatom/core"
import type { Atom } from "@reatom/core"
import type { MinimalDrop } from "./types"

/**
 * Atomized MinimalDrop structure
 * Each field is a separate atom for granular reactivity
 */
export type AtomizedDrop = {
  id: Atom<MinimalDrop["id"]>
  type: Atom<MinimalDrop["type"]>
  title: Atom<MinimalDrop["title"]>
  blockchain: Atom<MinimalDrop["blockchain"]>
  media: Atom<MinimalDrop["media"]>
  totalSupply: Atom<MinimalDrop["totalSupply"]>
  maxMintPerWallet: Atom<MinimalDrop["maxMintPerWallet"]>
  description: Atom<MinimalDrop["description"]>
  author: Atom<MinimalDrop["author"]>
  tokenStandard: Atom<MinimalDrop["tokenStandard"]>
  phases: Atom<MinimalDrop["phases"]>
  fees: Atom<MinimalDrop["fees"]>
  twitterLink?: Atom<NonNullable<MinimalDrop["twitterLink"]>>
  isVerified: Atom<MinimalDrop["isVerified"]>
}

/**
 * Atomizes a MinimalDrop object for granular reactivity
 * Each field becomes a separate atom to prevent unnecessary re-renders
 *
 * @param drop - The drop object to atomize
 * @param namePrefix - Optional prefix for atom names (for debugging)
 * @returns Atomized drop structure
 */
export function atomizeDrop(
  drop: MinimalDrop,
  namePrefix = "drop",
): Atom<AtomizedDrop> {
  return atom(
    {
      id: atom(drop.id, `${namePrefix}-${drop.id}-id`),
      type: atom(drop.type, `${namePrefix}-${drop.id}-type`),
      title: atom(drop.title, `${namePrefix}-${drop.id}-title`),
      blockchain: atom(drop.blockchain, `${namePrefix}-${drop.id}-blockchain`),
      media: atom(drop.media, `${namePrefix}-${drop.id}-media`),
      totalSupply: atom(
        drop.totalSupply,
        `${namePrefix}-${drop.id}-totalSupply`,
      ),
      maxMintPerWallet: atom(
        drop.maxMintPerWallet,
        `${namePrefix}-${drop.id}-maxMintPerWallet`,
      ),
      description: atom(
        drop.description,
        `${namePrefix}-${drop.id}-description`,
      ),
      author: atom(drop.author, `${namePrefix}-${drop.id}-author`),
      tokenStandard: atom(
        drop.tokenStandard,
        `${namePrefix}-${drop.id}-tokenStandard`,
      ),
      phases: atom(drop.phases, `${namePrefix}-${drop.id}-phases`),
      fees: atom(drop.fees, `${namePrefix}-${drop.id}-fees`),
      twitterLink: drop.twitterLink
        ? atom(drop.twitterLink, `${namePrefix}-${drop.id}-twitterLink`)
        : undefined,
      isVerified: atom(drop.isVerified, `${namePrefix}-${drop.id}-isVerified`),
    },
    `${namePrefix}-${drop.id}`,
  )
}
